<template>
  <div>
    <div class="partyInfoBox">
      <PartyInfo
        v-for="party of data[0]"
        :party="party"
        :currentView="currentView"
        :partyNum="Math.round(data[1][data[0].indexOf(party)])"
        :max="Math.round(Math.max(...data[1]))"
        :key="party"
      />
    </div>
    <div class="chartBox chartCursor">
      <div class="smallTitle" style="width: 100%">
        {{ $t("FederalView.mediumTitle1") }}
      </div>
      <div ref="chartDiv" class="chart-container"></div>
    </div>
    <div class="additional" style="text-align: left">
      <span style="font-weight: bold">{{ $t("FederalView.textTitle2") }}</span
      >{{ $t("FederalView.text6") }}
    </div>
  </div>
</template>
<script>
import PartyInfo from "./PartyInfo.vue";
import axios from "axios";

const SHEET_ID = "1VQNfGKNDw4cf1mXQpARDyg47ZIql5pDM9sBSEXMZ_gw";

export default {
  name: "OpinionGraph",
  components: {
    PartyInfo,
  },
  props: {
    currentView: String,
  },
  data() {
    return {
      data: [[], []],
      chart: null,
      chartData: null,
      resizeTimer: null,
    };
  },
  async mounted() {
    await axios
      .get(
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vSHdxj1KPFMHP5gT1ybNfd_-N_7QikiUQ1zMKJgGb4xZjwPY4DcRE6xjWHlWqgbhawjk8ZO6I52MakV/pub?gid=1804583268&single=true&output=csv",
      )
      .then((response) => (this.data = this.parseData(response.data)));

    this.loadGoogleCharts();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.resizeTimer);
  },
  methods: {
    loadGoogleCharts() {
      if (window.google && window.google.charts) {
        this.initChart();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://www.gstatic.com/charts/loader.js";
      script.onload = () => this.initChart();
      document.head.appendChild(script);
    },
    initChart() {
      window.google.charts.load("current", { packages: ["corechart"] });
      window.google.charts.setOnLoadCallback(() => this.fetchAndDraw());
    },
    fetchAndDraw() {
      axios
        .get(
          `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&gid=1505107173`,
        )
        .then((response) => {
          const rows = this.parsePollsCsv(response.data);
          this.chartData = window.google.visualization.arrayToDataTable(rows);
          this.drawChart();
        })
        .catch((err) => console.error("Failed to fetch poll data:", err));
    },
    parsePollsCsv(csv) {
      const lines = csv.trim().split(/\r?\n/);
      const raw = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split('","').map((c) => c.replace(/"/g, ""));
        const date = new Date(cols[0]);
        if (isNaN(date.getTime())) continue;
        if (!cols[1] && !cols[2] && !cols[3]) continue;
        raw.push([
          date,
          cols[1] !== "" ? parseFloat(cols[1]) : null,
          cols[2] !== "" ? parseFloat(cols[2]) : null,
          cols[3] !== "" ? parseFloat(cols[3]) : null,
          cols[4] !== "" ? parseFloat(cols[4]) : null,
          cols[5] !== "" ? parseFloat(cols[5]) : null,
        ]);
      }

      raw.sort((a, b) => a[0] - b[0]);

      // Deduplicate by date, then apply 14-day rolling average
      const byDate = new Map();
      for (const row of raw) {
        const rowDate = /** @type {Date} */ (row[0]);
        const key = rowDate.toDateString();
        if (!byDate.has(key))
          byDate.set(key, { date: rowDate, vals: [[], [], [], [], []] });
        for (let p = 0; p < 5; p++) {
          if (row[p + 1] !== null) byDate.get(key).vals[p].push(row[p + 1]);
        }
      }

      const dates = [...byDate.values()].sort((a, b) => a.date - b.date);
      const MS_14 = 14 * 24 * 60 * 60 * 1000;
      const tableRows = [["Date", "CPC", "LPC", "NDP", "BQ", "Green"]];

      for (let i = 0; i < dates.length; i++) {
        const { date } = dates[i];

        // 14-day rolling average
        const windowStart = new Date(date - MS_14);
        const sums = [0, 0, 0, 0, 0];
        const counts = [0, 0, 0, 0, 0];
        for (const d of dates) {
          if (d.date < windowStart || d.date > date) continue;
          for (let p = 0; p < 5; p++) {
            for (const v of d.vals[p]) {
              sums[p] += v;
              counts[p]++;
            }
          }
        }
        const avg = counts.map((c, p) => (c ? sums[p] / c : null));

        tableRows.push([date, avg[0], avg[1], avg[2], avg[3], avg[4]]);
      }

      return tableRows;
    },
    drawChart() {
      if (!this.chartData || !this.$refs.chartDiv) return;

      if (!this.chart) {
        this.chart = new window.google.visualization.ComboChart(
          this.$refs.chartDiv,
        );
      }

      const options = {
        backgroundColor: "#eeeeee",
        seriesType: "line",
        curveType: "function",
        interpolateNulls: true,
        focusTarget: "category",

        crosshair: {
          trigger: "both",
          orientation: "vertical",
          color: "#aaaaaa",
          opacity: 0.6,
        },
        legend: { position: "bottom" },
        lineWidth: 2.5,
        series: {
          0: { color: "#0F2D52" }, // CPC line
          1: { color: "#D71921" }, // LPC line
          2: { color: "#F58220" }, // NDP line
          3: { color: "#00A7EC" }, // BQ line
          4: { color: "#3C9B35" }, // Green line
        },
        vAxis: { minValue: 0 },
        hAxis: {
          slantedText: false,
          textStyle: { fontSize: 10 },
          gridlines: { color: "transparent" },
        },
        width: this.$refs.chartDiv.offsetWidth,
        height: 600,
        chartArea: { left: 50, right: 20, top: 20, bottom: 80 },
      };

      this.chart.draw(this.chartData, options);
    },
    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.drawChart(), 150);
    },
    parseData(data) {
      try {
        return data.split(/\r?\n/).map((line) => line.split(",").slice(1));
      } catch (error) {
        console.error("Error parsing data:", error);
        return [];
      }
    },
  },
};
</script>
<style scoped>
.partyInfoBox {
  display: flex;
  flex-wrap: wrap;
  align-items: left;
  justify-content: left;
}
.smallTitle {
  color: #2c3e50;
  font-size: 30px;
  font-weight: bold;
  font-family: Merriweather, serif;
  margin-top: 10px;
}
.chart-container {
  width: 100%;
  min-height: 600px;
}
</style>
